import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/AuthStore.js'
import { useMainStore } from '@/stores/MainStore.js'
import { useAnnouncementStore } from '@/stores/AnnouncementStore.js'
import Api from '@/api/Api';
import noop from 'lodash/noop';
import { useLoginRedirect } from '@/components/login-redirect.js';
import { resolveVisitorId } from '@/api/AxiosInstance.js';

export default {
  methods: {
    afterLoginMixin: async function() {
      const authStore = useAuthStore();

      // Reset the states after login
      getActivePinia()._s.forEach(store => store.$reset())

      // Update the list of tenants and access points
      let dbUser = await authStore.refreshDBUser();

      // Check if the user is logged in.
      if (!dbUser || !dbUser.email) {
        return;
      }

      const tenants = authStore.getTenants;

      // Set active rootNodeId - the first accesspoint that is found
      // The active tenant will be set automatically
      if (tenants && tenants[0] && tenants[0].accessPoints && tenants[0].accessPoints[0]) {
        authStore.setRootNodeId(tenants[0].accessPoints[0].node.id);
      }

      // update the abilities
      const activeAccesspoint = authStore.getActiveAccesspoint;
      if (activeAccesspoint && activeAccesspoint.group && activeAccesspoint.group.abilities) {
        this.$updateAbilities(activeAccesspoint.group.abilities);
      }

      // Check if any tenant requires MFA
      let mfaIsRequired = false;
      for (const tenant of tenants) {
        mfaIsRequired = mfaIsRequired || tenant.mfarequired;
      }

      const cognitoUser = await authStore.refreshCognitoUser();

      const visitorId = await resolveVisitorId();

      if (visitorId) {
        Api.user.startSession(dbUser.email, visitorId).catch(noop);
      }

      const mfaPref = await authStore.fetchMFAPreference();
      const isMfaEnabled = mfaPref && mfaPref.enabled && mfaPref.enabled.length > 0;

      if (mfaIsRequired && cognitoUser && !isMfaEnabled) {
        this.$router.push('/account/enable-mfa');
        return;
      }

      // determine the user's rootNodeId
      let rootNodeId = activeAccesspoint?.node?.id || null;

      // Redirect expired tenants to the contact page instead of subscription plans.
      if (authStore.tenantIsExpired) {
        this.$router.push('/account/tenant-expired');
        return;
      }

      // set default dark mode
      const mainStore = useMainStore();
      if (window.localStorage.isDarkMode === undefined) {
        mainStore.setDarkMode(dbUser?.settings?.theme === 'dark');
      }

      // Fetch announcements and check if auto-show is needed
      if (rootNodeId) {
        const announcementStore = useAnnouncementStore();
        await announcementStore.fetchAndCheckAutoShow();
      }

      let redirectTo = null;

      try {
        const { getAndClearLocation } = useLoginRedirect();
        const location = getAndClearLocation();

        if (location) {
          const redirectUrl = new URL(location, import.meta.env.VITE_APP_URL);
          redirectTo = `${redirectUrl.pathname}${redirectUrl.search}`;
        }
      } catch {
        redirectTo = null;
      }

      let route;

      if (redirectTo) {
        route = redirectTo;
      } else {
        route = '/node';

        if (rootNodeId) {
          route += '/' + rootNodeId;
        } else if (!this.$canListNodeAndPod()) {
          route = '/';
        }
      }

      // set the interface language
      if (dbUser.settings && dbUser.settings.language) {
        const language = dbUser.settings.language || 'en';

        window.localStorage.setItem('interfaceLanguage', language);
        this.$i18n.locale = language;
      }

      this.$router.push(route);
    }
  }
}
